/**
 * EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1
 * 
 * ImportFormatHintPanel - Guidance panel for project/shift email import
 * 
 * Features:
 * - Collapsible help section
 * - Bilingual templates (EN/FR) with copy buttons
 * - Clear format guidance
 * - Additional services note
 */

import React, { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  HelpCircle,
  FileText,
  Globe,
  Lightbulb,
  AlertCircle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

// English template
const ENGLISH_TEMPLATE = `Client:
Project:
Venue:
Area / Location:

Date – Start to End – Hours – Crew – Shift Name
Date – Start to End – Hours – Crew – Shift Name

Additional Services:
Forklift:
Truck:
Other Notes:`;

const ENGLISH_EXAMPLE = `Client: Three Sixty Events
Project: Three Sixty Events - Grimaldi 2026
Venue: Grimaldi Forum
Area: Diaghilev

Sat 20 June 2026 – 09:00 to 13:00 – 4 Hours – 8 Crew – Unload
Sat 20 June 2026 – 09:00 to 15:00 – 6 Hours – 4 Crew – Unload
Sun 21 June 2026 – 08:00 to 16:00 – 8 Hours – 2 Crew – Assist with build

Additional Services:
Forklift: 1
Truck: 1`;

// French template
const FRENCH_TEMPLATE = `Client :
Projet :
Lieu :
Zone / Localisation :

Date – Début à Fin – Heures – Équipe – Nom du shift

Services supplémentaires :
Chariot élévateur :
Camion :
Autres remarques :`;

const FRENCH_EXAMPLE = `Client : Three Sixty Events
Projet : Three Sixty Events - Grimaldi 2026
Lieu : Grimaldi Forum
Zone / Localisation : Diaghilev

Sam 20 Juin 2026 – 09:00 à 13:00 – 4 Heures – 8 Équipiers – Déchargement
Sam 20 Juin 2026 – 09:00 à 15:00 – 6 Heures – 4 Équipiers – Déchargement
Dim 21 Juin 2026 – 08:00 à 16:00 – 8 Heures – 2 Équipiers – Aide au montage

Services supplémentaires :
Chariot élévateur : 1
Camion : 1`;

// Copy button component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };
  
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="gap-2"
      data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}-btn`}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-emerald-400" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy {label}
        </>
      )}
    </Button>
  );
};

// Template display component
const TemplateBlock = ({ template, example, language }) => {
  return (
    <div className="space-y-4">
      {/* Template */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-muted-foreground">
            Template Format
          </h4>
          <CopyButton text={template} label={`${language} Template`} />
        </div>
        <pre className="p-3 bg-surface-base/50 border border-white/5 rounded-lg text-xs font-mono text-muted-foreground whitespace-pre-wrap overflow-x-auto">
          {template}
        </pre>
      </div>
      
      {/* Example */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-muted-foreground">
            Example Request
          </h4>
          <CopyButton text={example} label={`${language} Example`} />
        </div>
        <pre className="p-3 bg-primary/5 border border-primary/10 rounded-lg text-xs font-mono text-foreground whitespace-pre-wrap overflow-x-auto">
          {example}
        </pre>
      </div>
    </div>
  );
};

export function ImportFormatHintPanel({ className, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn("w-full", className)}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between p-3 h-auto bg-surface-elevated/30 hover:bg-surface-elevated/50 border border-white/5 rounded-lg"
          data-testid="import-format-hint-toggle"
        >
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-primary" />
            <span className="font-medium">How to format a project request</span>
            <Badge variant="outline" className="text-[10px] ml-2">
              EN / FR
            </Badge>
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-3">
        <div className="p-4 bg-surface-elevated/20 border border-white/5 rounded-lg space-y-4">
          {/* Quick Overview */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-400" />
              Quick Guide
            </h4>
            <ul className="space-y-1.5 text-sm text-muted-foreground pl-6 list-disc">
              <li>Both <strong>English</strong> and <strong>French</strong> formats are accepted</li>
              <li>Use the <strong>compact format</strong> for best parsing results</li>
              <li>Include client name, project name, venue, and shift details</li>
              <li>You can always review and correct imported data before confirmation</li>
            </ul>
          </div>
          
          {/* Additional Services Note */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong className="text-blue-300">Additional Services:</strong>
              <span className="text-blue-200/80 ml-1">
                Forklifts, trucks, vans, equipment, and notes may be included in your email. 
                These will be visible during review even if not fully auto-parsed.
              </span>
            </div>
          </div>
          
          {/* Bilingual Templates */}
          <Tabs defaultValue="english" className="w-full">
            <TabsList className="w-full grid grid-cols-2 mb-4">
              <TabsTrigger value="english" className="gap-2">
                <Globe className="h-3.5 w-3.5" />
                English
              </TabsTrigger>
              <TabsTrigger value="french" className="gap-2">
                <Globe className="h-3.5 w-3.5" />
                Français
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="english">
              <TemplateBlock 
                template={ENGLISH_TEMPLATE} 
                example={ENGLISH_EXAMPLE}
                language="English"
              />
            </TabsContent>
            
            <TabsContent value="french">
              <TemplateBlock 
                template={FRENCH_TEMPLATE} 
                example={FRENCH_EXAMPLE}
                language="French"
              />
            </TabsContent>
          </Tabs>
          
          {/* Format Tips */}
          <div className="pt-3 border-t border-white/5">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Format Tips
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Check className="h-3 w-3 text-emerald-400" />
                <span>Use "to" or "à" between times</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-3 w-3 text-emerald-400" />
                <span>Include crew count as number</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-3 w-3 text-emerald-400" />
                <span>One shift per line</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-3 w-3 text-emerald-400" />
                <span>Use full date format (e.g., Sat 20 June 2026)</span>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default ImportFormatHintPanel;
